// src/components/FloatingMiniPlayer.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, SkipBack, SkipForward, X, Maximize2, GripVertical } from 'lucide-react';
import './FloatingMiniPlayer.css';

const FloatingMiniPlayer = ({
  currentSong,
  isPlaying,
  onPlayPause,
  onNext,
  onPrevious,
  onClose,
  onExpand,
}) => {
  const [pos, setPos] = useState({
    x: window.innerWidth - 340,
    y: window.innerHeight - 88,
  });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ mx: 0, my: 0, px: 0, py: 0 });
  const ref = useRef(null);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const dx = e.clientX - dragStart.current.mx;
      const dy = e.clientY - dragStart.current.my;
      const w = ref.current?.offsetWidth || 320;
      const h = ref.current?.offsetHeight || 72;
      setPos({
        x: Math.max(0, Math.min(dragStart.current.px + dx, window.innerWidth - w)),
        y: Math.max(0, Math.min(dragStart.current.py + dy, window.innerHeight - h)),
      });
    };
    const onUp = () => setDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging]);

  if (!currentSong) return null;

  const title = currentSong.title?.length > 22
    ? currentSong.title.slice(0, 22) + '…'
    : currentSong.title;
  const artist = currentSong.channelTitle?.length > 18
    ? currentSong.channelTitle.slice(0, 18) + '…'
    : currentSong.channelTitle;

  return (
    <div
      ref={ref}
      className={`fmp${dragging ? ' fmp--dragging' : ''}`}
      style={{ left: pos.x, top: pos.y }}
    >
      {/* Drag handle */}
      <div
        className="fmp__drag"
        onMouseDown={(e) => {
          e.preventDefault();
          dragStart.current = { mx: e.clientX, my: e.clientY, px: pos.x, py: pos.y };
          setDragging(true);
        }}
        title="Drag"
      >
        <GripVertical size={16} color="#c0c4cc" />
      </div>

      {/* Thumbnail */}
      <img
        src={currentSong.thumbnail}
        alt={currentSong.title}
        className={`fmp__thumb${isPlaying ? ' fmp__thumb--playing' : ''}`}
        onClick={onExpand}
      />

      {/* Song info */}
      <div className="fmp__info" onClick={onExpand}>
        <span className="fmp__title">{title}</span>
        <span className="fmp__artist">{artist}</span>
      </div>

      {/* Playback controls */}
      <div className="fmp__controls">
        <button className="fmp__btn" onClick={onPrevious} title="Previous">
          <SkipBack size={15} />
        </button>
        <button className="fmp__btn fmp__btn--play" onClick={onPlayPause} title={isPlaying ? 'Pause' : 'Play'}>
          {isPlaying ? <Pause size={16} fill="white" /> : <Play size={16} fill="white" />}
        </button>
        <button className="fmp__btn" onClick={onNext} title="Next">
          <SkipForward size={15} />
        </button>
      </div>

      {/* Expand + Close */}
      <div className="fmp__actions">
        <button className="fmp__action-btn" onClick={onExpand} title="Open player">
          <Maximize2 size={13} />
        </button>
        <button className="fmp__action-btn fmp__action-btn--close" onClick={onClose} title="Close">
          <X size={13} />
        </button>
      </div>
    </div>
  );
};

export default FloatingMiniPlayer;